function filtersService(
  $location,
  $log,
  _,
  siteService
) {
  const service = {};

  /**
   * service flow
   * initialization
      * creates urlState and activeFilters
   * filter change
      * overwrites urlState
      * requests/makes change
   * transient service properties:
      * service.activeFilters
      * service.urlState
      * service.selfInitiatedChange
      * service.ctrlCallback
   */

  service.filters = {};

  service.defaultFormatter = value => value;

  service.intFormatter = value => parseInt(value, 10);

  service.sortFormatter = value => value || 'time';

  service.availableFilters = {
    publicationName: {
      param: 'pname',
      label: 'Publication Name'
    },
    taskSort: {
      param: 'tsort',
      fallback: 'createdDate',
      label: 'Task Sort'
    },
    taskType: {
      param: 'tt',
      label: 'Task Type'
    },
    taskStatus: {
      param: 'ts',
      label: 'Task Status'
    },
    taskPriority: {
      param: 'tp',
      label: 'Task Priority'
    },
    searchTerm: {
      param: 'term',
      label: 'Search Term'
    },
    searchTermCopy: {
      param: 'term',
      exclude: true
    },
    author: {
      param: 'author',
      label: 'Author (freelance)'
    },
    authorID: {
      param: 'at',
      label: 'Author'
    },
    headline: {
      param: 'hed',
      label: 'Headline'
    },
    slug: {
      param: 'slug',
      label: 'Slug'
    },
    sourceId: {
      param: 'sourceId',
      label: 'Source ID'
    },
    pitchStatus: {
      param: 'pi',
      label: 'Pitch Status'
    },
    publishStatus: {
      param: 'ps',
      label: 'Publish Status'
    },
    workflowStatus: {
      param: 'ws',
      label: 'Workflow Status'
    },
    contentType: {
      param: 'ct',
      label: 'Content Type'
    },
    direction: {
      param: 'sd',
      fallback: 'asc',
      label: 'Search Direction'
    },
    plan: {
      param: 'plan',
      label: 'Plan'
    },
    childPlans: {
      param: 'child_plans',
      label: 'Child Plans'
    },
    tbd: {
      param: 'tbd',
      label: 'To-Be-Decided'
    },
    sortOrder: {
      param: 'sort',
      fallback: 'time',
      label: 'Sort By'
    },
    startDate: {
      param: 'starting',
      fallback: 'today',
      label: 'Start Date'
    },
    endDate: {
      param: 'ending',
      defaultStartingValue: 'empty'
    },
    // Following start/end params just to be used for stats, update won't trigger unless registered
    start: {
      param: 'start'
    },
    end: {
      param: 'end'
    },
    groupBy: {
      param: 'groupby'
    },
    sections: {
      param: 'sec'
    },
    platforms: {
      param: 'pl'
    },
    plannedTimeAccuracy: {
      param: 'plannedTimeAccuracy'
    },
    quantity: {
      param: 'quantity'
    },
    sourceType: {
      param: 'so',
      label: 'Content Source',
      defaultStartingValue: 'staff'
    },
    distributorId: {
      param: 'dist'
    },
    distributorName: {
      param: 'distName'
    },
    sponsored: {
      param: 'content',
      label: 'Sponsored Content',
      defaultStartingValue: 'editorial'
    },
    live: {
      param: 'live'
    },
    pitchesOnly: {
      param: 'pitchesOnly'
    },
    tags: {
      param: 'tags'
    },
    createdBy: {
      param: 'createdBy',
      label: 'Created By'
    },
    websites: {
      param: 'websites',
      fallback: siteService.getSelectedWebsite() ? siteService.getSelectedWebsite()._id : ''
    },
    hierarchy: {
      param: 'hier',
      fallback: siteService.getSelectedWebsite() ? siteService.getSelectedWebsite()._id : ''
    }
  };

  service.defaultFilters = {};

  const filterKeys = Object.keys(service.availableFilters);

  filterKeys.forEach((filter) => {
    const filterObj = service.availableFilters[filter];
    if (filter !== 'taskSort' && (filterObj.defaultStartingValue || filterObj.fallback)) {
      service.defaultFilters[filterObj.param] = filterObj.defaultStartingValue ||
      filterObj.fallback;
    }
  });

  // asynchronously apply fallback to `hierarchy` and `websites`

  if (!siteService.getSelectedWebsite()) {
    siteService.getDefaultWebsite().then((res) => {
      if (res && Object.keys(res).length > 0 && res._id) {
        service.availableFilters.hierarchy.fallback = res._id;
        service.defaultFilters.hier = res._id;

        service.availableFilters.websites.fallback = res._id;
        service.defaultFilters.websites = res._id;
      }
    });
  }

  service.createFilterSet = (urlState, search) => new service.FilterSet(urlState, search);

  service.createFilterSetFromUrl = search => new service.FilterSet($location.search(), search);

  service.FilterSet = function FilterSet(url, search) {
    this.filters = {};
    this.search = search;
    if (url) {
      this.buildFilters(url);
    }
  };

  service.FilterSet.prototype.getNonFilterParams = function getNonFilterParams(urlState) {
    const params = {};
    _.forOwn(urlState, (value, key) => {
      if (_.startsWith(key, '_')) {
        params[key] = value;
      }
    });

    return params;
  };

  service.FilterSet.prototype.buildFilters = function buildFilters(urlState, rebuild) {
    const filterSet = this;

    if (rebuild) {
      filterSet.filters = _.forOwn(filterSet.filters, (value, key) => {
        if (!service.availableFilters[key] || !urlState[service.availableFilters[key].param]) {
          const fallbackValue = service.availableFilters[key] ?
            service.availableFilters[key].fallback : null;
          filterSet.filters[key].value = fallbackValue || null;
        }
      });
    }

    /**
     * Use filterSet.search.params instead of urlState if urlState state is empty object
     * (in the case of loading a saved search from homepage)
     */
    const objIterator = Object.keys(urlState).length === 0 && filterSet.search ?
      filterSet.search.params : urlState;

    _.forOwn(objIterator, (value, key) => {
      if (!_.startsWith(key, '_')) {
        const availableFilter = _.find(service.availableFilters, i => i.param === key);

        const existingFilter = _.find(filterSet.filters, i => i.param === key);

        if (existingFilter) {
          existingFilter.value = value || existingFilter.fallback;
        } else if (availableFilter) {
          const filterInstance = new service.Filter(_.findKey(service.availableFilters,
            availableFilter), value || availableFilter.fallback, filterSet);
          filterSet.filters[filterInstance.name] = filterInstance;
        } else {
          const filterInstance = new service.Filter(key, value);
          filterSet.filters[filterInstance.name] = filterInstance;
        }
      }
    });

    return this.filters;
  };

  service.FilterSet.prototype.buildQueryParams = function buildQueryParams(filters) {
    const toBuildFrom = filters || this.filters;

    return _.reduce(toBuildFrom, (queryParams, value) => {
      const queryParamsCopy = queryParams;
      if (value && value.value && !value.exclude) queryParamsCopy[value.param] = value.value;
      return queryParamsCopy;
    }, {});
  };

  service.FilterSet.prototype.getFilter = function getFilter(name, value) {
    if (!this.filters[name]) {
      this.filters[name] = new service.Filter(name, value || (service.availableFilters[name] ?
        service.availableFilters[name].fallback : null), this);
    } else if (value) {
      this.filters[name].value = value;
    }

    return this.filters[name];
  };

  service.FilterSet.prototype.getFilterValue = function getFilterValue(name) {
    return this.filters[name] ? this.filters[name].value : null;
  };

  service.FilterSet.prototype.getActiveFiltersSet = function getFilterSet() {
    return _.reduce(this.filters, (resultObj, filter, filterName) => {
      const result = resultObj;
      if ((_.isArray(filter.value) && !_.isEmpty(filter.value)) ||
        (!_.isArray(filter.value) && (filter.value || filter.value === false || filter.fallback))
      ) result[filterName] = filter;
      return result;
    }, {});
  };

  /**
  * Updates multiple filters at once, triggering only one search request
  * @param {Object.<string, ?(string|number|string[]|number[])>} filterUpdate
  */
  service.FilterSet.prototype.changeValues = function changeValues(filterUpdate) {
    Object.keys(filterUpdate).forEach((filter) => {
      this.getFilter(filter).value = filterUpdate[filter];
    });

    if (this.search && this.search.triggerSearchUpdate) {
      this.search.triggerSearchUpdate.call(this.search);
    }
  };

  service.Filter = function Filter(name, value, filterSet) {
    this.param = service.availableFilters[name] ? service.availableFilters[name].param : name;

    if (service.availableFilters[name]) {
      this.fallback = service.availableFilters[name].fallback;
    }

    this.name = name;
    this.value = value || this.fallback || null;

    if (filterSet && filterSet.search && filterSet.search.triggerSearchUpdate) {
      this.triggerUpdate = filterSet.search.triggerSearchUpdate.bind(filterSet.search);
    }

    if (service.availableFilters[name]) {
      this.label = service.availableFilters[name].label;
    }
  };

  service.Filter.prototype.changeValue = function changeValue(newValue) {
    this.value = newValue || this.fallback || null;

    if (this.name === 'hierarchy' || this.name === 'websites') {
      service.availableFilters[this.name].fallback = this.value;
      service.defaultFilters[this.param] = this.value;
    }
    $log.info(`Filter ${this.name} changed to value ${this.value}`);

    if (this.triggerUpdate) {
      this.triggerUpdate();
    }
  };

  // return the service object
  return service;
}

const arr = [
  '$location',
  '$log',
  '_',
  'siteService',
  filtersService
];

module.exports = (ngModule) => {
  ngModule.service('filtersService', arr);
};
